/**
 * Content Management Enums
 *
 * All enums related to content management functionality.
 * These are shared between frontend and backend to ensure consistency.
 */

export enum BrandType {
  PERSONAL = "personal",
  BUSINESS = "business",
  COMPANY = "company",
  AGENCY = "agency",
}

export enum ContentStatus {
  DRAFT = "draft",
  READY = "ready",
  SCHEDULED = "scheduled",
  PUBLISHED = "published",
  ARCHIVED = "archived",
  FAILED = "failed",
}

export enum Platform {
  LINKEDIN = "linkedin",
  TWITTER = "twitter",
  FACEBOOK = "facebook",
  INSTAGRAM = "instagram",
}

export enum PostType {
  SINGLE_POST = "single_post",
  THREAD = "thread",
  CAROUSEL = "carousel",
  VIDEO = "video",
}

export enum ApprovalStatus {
  PENDING = "pending",
  APPROVED_IDEA = "approved_idea",
  APPROVED_TEMPLATE = "approved_template",
  REJECTED = "rejected",
}

export enum Priority {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
}

export enum IdeaStatus {
  PENDING = "pending",
  PROCESSED = "processed",
  USED = "used",
}

export enum ScheduledPostStatus {
  SCHEDULED = "scheduled",
  POSTING = "posting",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELLED = "cancelled",
}

export enum SourceType {
  ORIGINAL = "original",
  SCRAPED = "scraped",
  VOICE_NOTE = "voice_note",
  TEMPLATE = "template",
  IDEA = "idea",
}

export enum TemplateCategory {
  GENERAL = "general",
  PRODUCT_LAUNCH = "product_launch",
  THOUGHT_LEADERSHIP = "thought_leadership",
  ENGAGEMENT = "engagement",
  ANNOUNCEMENT = "announcement",
  PERSONAL_STORY = "personal_story",
  TIPS_ADVICE = "tips_advice",
  BEHIND_SCENES = "behind_scenes",
}

export enum ScrapingFrequency {
  MANUAL = "manual",
  DAILY = "daily",
  WEEKLY = "weekly",
  MONTHLY = "monthly",
}

// Helper functions to get enum values as arrays for Zod validation
export const getBrandTypeValues = () => Object.values(BrandType) as [string, ...string[]]
export const getContentStatusValues = () => Object.values(ContentStatus) as [string, ...string[]]
export const getPlatformValues = () => Object.values(Platform) as [string, ...string[]]
export const getPostTypeValues = () => Object.values(PostType) as [string, ...string[]]
export const getIdeaStatusValues = () => Object.values(IdeaStatus) as [string, ...string[]]
export const getApprovalStatusValues = () => Object.values(ApprovalStatus) as [string, ...string[]]
export const getPriorityValues = () => Object.values(Priority) as [string, ...string[]]
export const getScheduledPostStatusValues = () => Object.values(ScheduledPostStatus) as [string, ...string[]]
export const getSourceTypeValues = () => Object.values(SourceType) as [string, ...string[]]
export const getTemplateCategoryValues = () => Object.values(TemplateCategory) as [string, ...string[]]
export const getScrapingFrequencyValues = () => Object.values(ScrapingFrequency) as [string, ...string[]]

// Helper functions to get display names
export const getBrandTypeLabel = (type: BrandType): string => {
  switch (type) {
    case BrandType.PERSONAL:
      return "Personal"
    case BrandType.BUSINESS:
      return "Business"
    case BrandType.COMPANY:
      return "Company"
    case BrandType.AGENCY:
      return "Agency"
    default:
      return type
  }
}

export const getContentStatusLabel = (status: ContentStatus): string => {
  switch (status) {
    case ContentStatus.DRAFT:
      return "Draft"
    case ContentStatus.READY:
      return "Ready"
    case ContentStatus.SCHEDULED:
      return "Scheduled"
    case ContentStatus.PUBLISHED:
      return "Published"
    case ContentStatus.ARCHIVED:
      return "Archived"
    case ContentStatus.FAILED:
      return "Failed"
    default:
      return status
  }
}

export const getPlatformLabel = (platform: Platform): string => {
  switch (platform) {
    case Platform.LINKEDIN:
      return "LinkedIn"
    case Platform.TWITTER:
      return "Twitter/X"
    case Platform.FACEBOOK:
      return "Facebook"
    case Platform.INSTAGRAM:
      return "Instagram"
    default:
      return platform
  }
}

export const getPlatformIcon = (platform: Platform): string => {
  switch (platform) {
    case Platform.LINKEDIN:
      return "💼"
    case Platform.TWITTER:
      return "🐦"
    case Platform.FACEBOOK:
      return "📘"
    case Platform.INSTAGRAM:
      return "📸"
    default:
      return "📱"
  }
}

export const getPriorityLabel = (priority: Priority): string => {
  switch (priority) {
    case Priority.LOW:
      return "Low"
    case Priority.MEDIUM:
      return "Medium"
    case Priority.HIGH:
      return "High"
    default:
      return priority
  }
}

export const getTemplateCategoryLabel = (category: TemplateCategory): string => {
  switch (category) {
    case TemplateCategory.GENERAL:
      return "General"
    case TemplateCategory.PRODUCT_LAUNCH:
      return "Product Launch"
    case TemplateCategory.THOUGHT_LEADERSHIP:
      return "Thought Leadership"
    case TemplateCategory.ENGAGEMENT:
      return "Engagement"
    case TemplateCategory.ANNOUNCEMENT:
      return "Announcement"
    case TemplateCategory.PERSONAL_STORY:
      return "Personal Story"
    case TemplateCategory.TIPS_ADVICE:
      return "Tips & Advice"
    case TemplateCategory.BEHIND_SCENES:
      return "Behind the Scenes"
    default:
      return (category as string).replace("_", " ")
  }
}

export const getScrapingFrequencyLabel = (frequency: ScrapingFrequency): string => {
  switch (frequency) {
    case ScrapingFrequency.MANUAL:
      return "Manual Only"
    case ScrapingFrequency.DAILY:
      return "Daily"
    case ScrapingFrequency.WEEKLY:
      return "Weekly"
    case ScrapingFrequency.MONTHLY:
      return "Monthly"
    default:
      return frequency
  }
}
